import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { WishlistProvider } from "@/contexts/WishlistContext";
import { LocationProvider } from "@/contexts/LocationContext";
import { QuoteDrawerProvider } from "@/contexts/QuoteDrawerContext";
import { SuggestProductDrawerProvider } from "@/contexts/SuggestProductDrawerContext";
import { CheckoutStepProvider } from "@/contexts/CheckoutStepContext";

import AppShell from "@/components/layout/AppShell";
import AuthDialog from "@/components/auth/AuthDialog";
import LocationDialog from "@/components/layout/LocationDialog";
import QuoteDrawer from "@/components/quote/QuoteDrawer";
import SuggestProductDrawer from "@/components/quote/SuggestProductDrawer";

import Home from "@/pages/Home";
import CategoryPage from "@/pages/CategoryPage";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Events from "@/pages/Events";
import Rewards from "@/pages/Rewards";
import Support from "@/pages/Support";
import Placeholder from "@/pages/Placeholder";
import InfoPage from "@/pages/InfoPage";
import Partner from "@/pages/Partner";
import Wishlist from "@/pages/Wishlist";
import NotificationsPage from "@/pages/Notifications";
import BrandsPage from "@/pages/Brands";
import Consultation from "@/pages/Consultation";
import ConsultationBook from "@/pages/ConsultationBook";
import ClinicSetup from "@/pages/ClinicSetup";
import HireHub from "@/pages/HireHub";
import AiConsultantDashboard from "@/pages/AiConsultantDashboard";
import AiConsultantNew from "@/pages/AiConsultantNew";
import AiConsultantWorkspace from "@/pages/AiConsultantWorkspace";
import AiConsultantReport from "@/pages/AiConsultantReport";
import RequireAuth from "@/components/auth/RequireAuth";
import AccountLayout from "@/components/layout/AccountLayout";
import AccountProfile from "@/pages/account/Profile";
import AccountAddresses from "@/pages/account/Addresses";
import AccountRewardHistory from "@/pages/account/RewardHistory";
import AccountRewardZone from "@/pages/account/RewardZone";
import AccountWishlist from "@/pages/account/Wishlist";
import AccountOrders from "@/pages/account/Orders";
import AccountReturns from "@/pages/account/Returns";
import AccountCoupons from "@/pages/account/Coupons";
import AccountMembership from "@/pages/account/Membership";
import AccountReferEarn from "@/pages/account/ReferEarn";
import AccountHelp from "@/pages/account/Help";
import OrderConfirmation from "@/pages/OrderConfirmation";

function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
            <LocationProvider>
            <QuoteDrawerProvider>
            <SuggestProductDrawerProvider>
            <CheckoutStepProvider>
            <AppShell>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<CategoryPage />} />
                <Route path="/category/:slug" element={<CategoryPage />} />
                <Route path="/brand/:slug" element={<CategoryPage mode="brand" />} />
                <Route path="/brands" element={<BrandsPage />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order/:orderNo" element={<OrderConfirmation />} />
                <Route path="/wishlist" element={<Wishlist />} />
                <Route path="/notifications" element={<NotificationsPage />} />

                {/* Services routes */}
                <Route path="/consultation" element={<Consultation />} />
                <Route path="/consultation/book/:id" element={<ConsultationBook />} />
                <Route path="/hire-hub" element={<HireHub />} />
                <Route path="/staffing" element={<HireHub />} />
                <Route path="/ai-consultant" element={<RequireAuth><AiConsultantDashboard /></RequireAuth>} />
                <Route path="/ai-consultant/new" element={<RequireAuth><AiConsultantNew /></RequireAuth>} />
                <Route path="/ai-consultant/:caseId/report" element={<RequireAuth><AiConsultantReport /></RequireAuth>} />
                <Route path="/ai-consultant/:caseId" element={<RequireAuth><AiConsultantWorkspace /></RequireAuth>} />
                <Route path="/clinic-setup" element={<ClinicSetup />} />
                {/* Legacy alias */}
                <Route path="/dr-dyntra" element={<RequireAuth><AiConsultantDashboard /></RequireAuth>} />

                <Route path="/events" element={<Events />} />
                <Route path="/rewards" element={<Rewards />} />
                <Route path="/support" element={<Support />} />
                <Route path="/help" element={<Support />} />

                <Route path="/refurbished" element={<CategoryPage />} />
                <Route path="/offers" element={<CategoryPage />} />
                <Route path="/orders" element={<Navigate to="/account/orders" replace />} />
                <Route path="/partner" element={<Partner />} />

                {/* My Account hub */}
                <Route
                  path="/account"
                  element={
                    <RequireAuth>
                      <AccountLayout />
                    </RequireAuth>
                  }
                >
                  <Route index element={<Navigate to="profile" replace />} />
                  <Route path="profile" element={<AccountProfile />} />
                  <Route path="addresses" element={<AccountAddresses />} />
                  <Route path="reward-history" element={<AccountRewardHistory />} />
                  <Route path="reward-zone" element={<AccountRewardZone />} />
                  <Route path="wishlist" element={<AccountWishlist />} />
                  <Route path="orders" element={<AccountOrders />} />
                  <Route path="returns" element={<AccountReturns />} />
                  <Route path="coupons" element={<AccountCoupons />} />
                  <Route path="membership" element={<AccountMembership />} />
                  <Route path="refer" element={<AccountReferEarn />} />
                  <Route path="help" element={<AccountHelp />} />
                </Route>

                {/* Company / Legal / Support info pages */}
                <Route path="/about" element={<InfoPage slug="about" />} />
                <Route path="/careers" element={<InfoPage slug="careers" />} />
                <Route path="/blog" element={<InfoPage slug="blog" />} />
                <Route path="/contact" element={<InfoPage slug="contact" />} />
                <Route path="/faqs" element={<InfoPage slug="faqs" />} />
                <Route path="/policy/shipping" element={<InfoPage slug="policy/shipping" />} />
                <Route path="/policy/return" element={<InfoPage slug="policy/return" />} />
                <Route path="/policy/privacy" element={<InfoPage slug="policy/privacy" />} />
                <Route path="/policy/terms" element={<InfoPage slug="policy/terms" />} />
                <Route path="/sitemap" element={<InfoPage slug="sitemap" />} />

                <Route path="*" element={<Placeholder title="Page Not Found" description="The page you're looking for doesn't exist yet." />} />
              </Routes>
            </AppShell>
            <AuthDialog />
            <LocationDialog />
            <QuoteDrawer />
            <SuggestProductDrawer />
            <Toaster position="top-right" richColors />
            </CheckoutStepProvider>
            </SuggestProductDrawerProvider>
            </QuoteDrawerProvider>
            </LocationProvider>
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
